import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, catchError, of, take } from 'rxjs';
import {
  LucideAngularModule,
  Calendar,
  Clock,
  ArrowLeft,
  Info,
  ChevronDown,
  ChevronUp,
} from 'lucide-angular';
import { AssignmentService } from '../../services/assignment.service';
import { ASSIGNMENT_THEME_COLOR } from '../../services/assignment.models';
import { AuthService } from '../../../../../common/auth/auth.service';
import { AmplitudeService } from '../../../../../common/analytics/amplitude.service';
import { CalendarPickerComponent } from './calendar-picker/calendar-picker';

interface DueDateOption {
  value: string; // 'YYYY-MM-DD' date string, '' for no due date, 'custom' for custom picker
  label: string;
}

@Component({
  selector: 'app-step-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    CalendarPickerComponent,
  ],
  templateUrl: './step-settings.html',
  styleUrl: './step-settings.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepSettingsComponent implements OnInit {
  private readonly assignmentService = inject(AssignmentService);
  private readonly authService = inject(AuthService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  @Output() readonly next = new EventEmitter<void>();
  @Output() readonly back = new EventEmitter<void>();

  protected readonly themeColor = ASSIGNMENT_THEME_COLOR;
  protected readonly error$ = this.assignmentService.error$;

  protected readonly icons = {
    Calendar,
    Clock,
    ArrowLeft,
    Info,
    ChevronDown,
    ChevronUp,
  };

  // ─── Info popover state ────────────────────────────────────

  protected showDueDateInfo = false;
  protected showTimeLimitInfo = false;

  // ─── Form state ──────────────────────────────────────────

  protected dueDate = '';
  protected timeLimit = 15;
  protected isCustomDueDate = false;
  protected isCalendarOpen = false;

  // ─── Due date options ──────────────────────────────────────

  protected dueDateOptions: DueDateOption[] = [];
  protected showMoreDueDates = false;

  protected get visibleDueDateOptions(): DueDateOption[] {
    if (this.showMoreDueDates) {
      return this.dueDateOptions;
    }
    return this.dueDateOptions.filter(
      (o) =>
        o.label === 'No due date' ||
        o.label === 'Today' ||
        o.label === 'Tomorrow' ||
        o.value === 'custom',
    );
  }

  protected get hasHiddenDueDates(): boolean {
    return this.dueDateOptions.length > this.visibleDueDateOptions.length;
  }

  protected toggleShowMoreDueDates(): void {
    this.showMoreDueDates = !this.showMoreDueDates;
  }

  // ─── Saving state ────────────────────────────────────────

  private readonly isSaving$$ = new BehaviorSubject<boolean>(false);
  protected readonly isSaving$ = this.isSaving$$.asObservable();

  // ─── Time limit options ──────────────────────────────────

  protected readonly timeLimitOptions = [
    { value: 0, label: 'No limit' },
    { value: 3, label: '3m' },
    { value: 5, label: '5m' },
    { value: 10, label: '10m' },
    { value: 15, label: '15m' },
    { value: 20, label: '20m' },
    { value: 30, label: '30m' },
    { value: 45, label: '45m' },
    { value: 60, label: '60m' },
    { value: 90, label: '90m' },
    { value: 120, label: '2h' },
    { value: 180, label: '3h' },
    { value: 300, label: '5h' },
  ];

  private readonly VISIBLE_TIME_LIMIT_VALUES = [0, 15, 30, 60];
  protected showMoreTimeLimits = false;

  protected get visibleTimeLimitOptions() {
    if (this.showMoreTimeLimits) {
      return this.timeLimitOptions;
    }
    return this.timeLimitOptions.filter((o) =>
      this.VISIBLE_TIME_LIMIT_VALUES.includes(o.value),
    );
  }

  protected get hasHiddenTimeLimits(): boolean {
    return (
      this.timeLimitOptions.length > this.VISIBLE_TIME_LIMIT_VALUES.length
    );
  }

  protected toggleShowMoreTimeLimits(): void {
    this.showMoreTimeLimits = !this.showMoreTimeLimits;
  }

  // ─── Lifecycle ───────────────────────────────────────────

  ngOnInit(): void {
    this.buildDueDateOptions();

    // Default to "Tomorrow"
    const tomorrowOption = this.dueDateOptions.find(
      (o) => o.label === 'Tomorrow',
    );
    if (tomorrowOption) {
      this.dueDate = tomorrowOption.value;
    }
  }

  // ─── Due date options ────────────────────────────────────

  private buildDueDateOptions(): void {
    const options: DueDateOption[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const shortDayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const shortMonthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    // No due date
    options.push({ value: '', label: 'No due date' });

    // Today
    options.push({
      value: this.formatDateForInput(today),
      label: 'Today',
    });

    // Tomorrow
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    options.push({
      value: this.formatDateForInput(tomorrow),
      label: 'Tomorrow',
    });

    // Next 5 days (day+2 through day+6)
    for (let i = 2; i <= 6; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const dayName = shortDayNames[d.getDay()];
      const dayNum = d.getDate();
      const month = shortMonthNames[d.getMonth()];
      options.push({
        value: this.formatDateForInput(d),
        label: `${dayName} ${dayNum} ${month}`,
      });
    }

    // Custom
    options.push({ value: 'custom', label: 'Custom...' });

    this.dueDateOptions = options;
  }

  protected selectDueDateOption(option: DueDateOption): void {
    if (option.value === 'custom') {
      this.isCustomDueDate = true;
      this.isCalendarOpen = false;
    } else {
      this.isCustomDueDate = false;
      this.dueDate = option.value;
      this.isCalendarOpen = false;
    }
  }

  protected isDueDateOptionSelected(option: DueDateOption): boolean {
    if (option.value === 'custom') {
      return this.isCustomDueDate;
    }
    return !this.isCustomDueDate && this.dueDate === option.value;
  }

  // ─── Calendar events ───────────────────────────────────────

  protected toggleCalendar(): void {
    this.isCalendarOpen = !this.isCalendarOpen;
  }

  protected onCalendarDateSelected(date: string): void {
    this.dueDate = date;
    this.isCalendarOpen = false;
  }

  protected onCalendarClosed(): void {
    this.isCalendarOpen = false;
  }

  // ─── Helpers ─────────────────────────────────────────────

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  protected formatDateForDisplay(dateStr: string): string {
    if (!dateStr) {
      return '';
    }
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  // ─── Navigation ──────────────────────────────────────────

  protected onBack(): void {
    this.assignmentService.setStep(3);
  }

  protected onSaveSettings(): void {
    this.isSaving$$.next(true);

    this.assignmentService
      .saveSettings({
        dueDate: this.dueDate || null,
        timeLimit: Number(this.timeLimit),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSaving$$.next(false);

          this.amplitudeService.trackEvent(
            'assignment_wizard_step_settings_completed',
            {
              assignment_id: this.assignmentService.assignmentId,
              has_due_date: !!this.dueDate,
              time_limit_minutes: Number(this.timeLimit),
            },
          );

          this.checkAuthAndProceed();
        },
        error: (err: any) => {
          this.isSaving$$.next(false);
          this.amplitudeService.trackEvent('assignment_wizard_error', {
            assignment_id: this.assignmentService.assignmentId,
            error_type: 'settings_save_failed',
            error: err?.message || 'Unknown error',
            step: 4,
          });
        },
      });
  }

  private checkAuthAndProceed(): void {
    this.authService
      .isAuthenticated()
      .pipe(take(1))
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this.assignAndPublish();
        } else {
          const assignmentId = this.assignmentService.assignmentId;

          this.amplitudeService.trackEvent('assignment_auth_redirect', {
            assignment_id: assignmentId,
          });

          if (assignmentId) {
            localStorage.setItem('pending_assignment_id', assignmentId);
          }

          const redirectUrl = `/assignment/${assignmentId}?publish=true`;
          this.router.navigate(['/sign-up'], {
            queryParams: { redirectUrl, userType: 'teacher' },
          });
        }
      });
  }

  private assignAndPublish(): void {
    this.isSaving$$.next(true);

    this.assignmentService
      .assignToUser()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((err: any) => {
          this.amplitudeService.trackEvent('assignment_wizard_error', {
            assignment_id: this.assignmentService.assignmentId,
            error_type: 'assign_to_user_failed',
            error: err?.message || 'Unknown error',
            step: 4,
          });
          return of(undefined);
        }),
      )
      .subscribe({
        next: () => {
          this.publishAndNavigateToReady();
        },
        error: () => {
          this.isSaving$$.next(false);
        },
      });
  }

  private publishAndNavigateToReady(): void {
    this.assignmentService
      .publishAssignment()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSaving$$.next(false);

          const inputData = this.assignmentService.inputData;
          this.amplitudeService.trackEvent('assignment_published', {
            assignment_id: this.assignmentService.assignmentId,
            grade: inputData.grade,
            subject: inputData.tier2SubjectId,
            total_questions: this.assignmentService.questions.length,
            has_due_date: !!this.dueDate,
            time_limit_minutes: Number(this.timeLimit),
          });

          this.assignmentService.setStep(5);
          this.next.emit();
        },
        error: (err: any) => {
          this.isSaving$$.next(false);
          this.amplitudeService.trackEvent('assignment_wizard_error', {
            assignment_id: this.assignmentService.assignmentId,
            error_type: 'publish_failed',
            error: err?.message || 'Unknown error',
            step: 4,
          });
        },
      });
  }
}
